import { useTextCustomField } from '@atlassian/jira-issue-field-single-line-text/src/services/index.tsx';
import { useSummaryField } from '@atlassian/jira-issue-field-summary-common/src/services/index.tsx';
import { SUMMARY_TYPE, TEXT_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component/index.tsx';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler/index.tsx';
import { assertClipboardHasPlainTextFormat } from '../../../clipboard-handler/utils.tsx';
import type {
	ClipboardItem,
	ListIssueForCopyPaste,
	PasteHandler,
	ListIssueFieldTransformed,
} from '../../../types.tsx';
import type { TransformedTextField } from '../types.tsx';

const getPastedValue = (clipboardItem: ClipboardItem): string | null => {
	if (!assertClipboardHasPlainTextFormat(clipboardItem)) {
		return null;
	}

	return clipboardItem.plainText;
};

function assertTextField(field: ListIssueFieldTransformed): asserts field is TransformedTextField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== TEXT_CF_TYPE && field.type !== SUMMARY_TYPE) {
		throw new Error('Field type is not supported');
	}
}

const useSummaryClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertTextField(field);

	const [{ value }, { saveValue }] = useSummaryField({
		// Dataset's in integration tests defined issueId as number,
		// which allows to bypass the type check
		issueId: issue.issueId.toString(),
		issueKey: issue.key,
		initialValue: field.value,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value,
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const useTextClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertTextField(field);

	const [{ value }, { saveValue }] = useTextCustomField({
		issueKey: issue.key,
		fieldKey: field.fieldId,
		initialValue: field.value,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value,
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const SummaryClipboardHelper = createClipboardHelperComponent(useSummaryClipboardHandler);
const TextClipboardHelper = createClipboardHelperComponent(useTextClipboardHandler);

export const textClipboardHandler = {
	[SUMMARY_TYPE]: SummaryClipboardHelper,
	[TEXT_CF_TYPE]: TextClipboardHelper,
};
